import React, { useEffect, useState } from "react";
import Steppers from "./Steppers";
import { useHistory, useLocation } from "react-router-dom";
import Layout from "../../pages/Layout";
import { Box, Container, Grid, Paper } from "@mui/material";
import pageStyles from "../../pages/pageStyles";
import { Form, Formik, FormikProps, FormikValues } from "formik";
import SelectItems from "./steps/SelectItems";
import UpdateData, { DynamicFieldProps } from "./steps/BulkUpdateData";
import ErrorMessageBox from "./ErrorMessage";
import ConfirmLeaveDialog from "./ConfirmLeaveDialog";
import * as Yup from "yup";
import ActionButtons from "./ActionButtons";
import { GeneralDataItem, bulkUpdate } from "../../repo/api";
import ShowResult from "./steps/ShowResult";
import { AxiosResponse } from "axios";
import useUserAccess from "../../effects/useUserAccess";
import { get } from "lodash";
import { MRT_ColumnDef } from "material-react-table";
import { styled } from "@mui/system";

export interface BulkUpdateData<T> {
  pathname: string;
  state: FilterQueryProps<T>;
}

export interface FilterQueryProps<T> {
  filterQuery: FormikValues;
  updateFields?: DynamicFieldProps[];
  tableColumns: MRT_ColumnDef<T>[];
  type?: string;
}

export type TableColumns = {
  title: string;
  field: string;
};

interface FieldValue {
  name: string;
  value: string;
}

export interface FormValues {
  fields: FieldValue[];
}

type FormattedValues = {
  [key: string]: any;
};

export enum Step {
  Selecting = 0,
  Updating = 1,
  Result = 2,
}

const StyledForm = styled(Form)({
  position: "relative",
  width: "100%",
  maxHeight: "600px",
  overflowY: "auto",
});

const validEntities = ["terminal", "merchant"]; // Add more entity/model here

export default function BulkUpdate() {
  const classes = pageStyles();
  const location = useLocation<FilterQueryProps<unknown>>();
  const history = useHistory();
  const access = useUserAccess();
  const isBulkUpdateAccessAllowed = get(access, `BulkUpdate.Access.U`) || false;

  const [activeStep, setActiveStep] = useState(Step.Selecting);
  const [selectedItems, setSelectedItems] = useState({});
  const [tableData, setTableData] = useState<GeneralDataItem[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [updateResult, setUpdateResult] = useState<AxiosResponse | undefined>(
    undefined
  );

  const filterQuery = location?.state?.filterQuery;
  const model = filterQuery?.entity;
  const tableColumns = location?.state?.tableColumns;
  const updateFields = location?.state?.updateFields || [];
  const terminalType = location?.state?.type || "Terminal";

  // LEAVING WARNING IF RERESH PAGE
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isProcessing) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isProcessing]);

  const handleConfirmLeaving = () => {
    setIsProcessing(false);
    setShowConfirmation(false);
    history.goBack();
  };

  const handleCancelLeaving = () => {
    setShowConfirmation(false);
  };

  // OTHER FUNCTIONS
  const capitalize = (s: string) => s && s[0].toUpperCase() + s.slice(1);

  // STEPS PROCESSING
  function getSteps() {
    return [
      `Select ${model ? capitalize(model) : "Items"}`,
      "Updating",
      "Save & Result",
    ]; // steps
  }
  const steps = getSteps();

  const getStepContent = (step: Step) => {
    switch (step) {
      case Step.Selecting:
        return (
          <SelectItems
            filterQuery={filterQuery}
            tableColumns={tableColumns}
            tableData={tableData}
            selectedItems={selectedItems}
            setTableData={setTableData}
            setSelectedItems={setSelectedItems}
          />
        );
      case Step.Updating:
        return <UpdateData updateFields={updateFields} />;
      case Step.Result:
        return <ShowResult isLoading={isLoading} updateResult={updateResult} />;
      default:
        return (
          <ErrorMessageBox message="Something wrong with the process. Please try again!" />
        );
    }
  };

  // Navigation functions
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // FORM
  const initialValues: FormValues = {
    fields: [
      {
        name: updateFields[0]?.name || "",
        value: "",
      },
    ],
  };

  const validationSchema = Yup.object().shape({
    fields: Yup.array().of(
      Yup.object().shape({
        name: Yup.string(),
        value: Yup.mixed().test({
          name: "customValidation",
          test: function(value) {
            const { parent } = this;
            const fieldItem = updateFields.find(
              (field) => field.name === parent.name
            );
            if (fieldItem?.validationSchema) {
              try {
                fieldItem.validationSchema.validateSync(value);
              } catch (error) {
                return this.createError({ message: error.message });
              }
            }
            return true;
          },
        }),
      })
    ),
  });

  const handleSubmit = (values: FormValues) => {
    setActiveStep(Step.Result);
    setIsLoading(true);

    const formattedValues = values.fields.reduce(
      (acc: FormattedValues, field) => {
        if (field.name && field.value) {
          acc[field.name] = field.value;
        }
        return acc;
      },
      {}
    );

    const bulkUpdateProcess = bulkUpdate({
      model,
      filter: filterQuery.values,
      updatedData: formattedValues,
      terminalType,
      excludedIds:
        tableData
          .filter((item, idx) => selectedItems[idx] !== true)
          .map((item) => item.id) || [],
    });

    bulkUpdateProcess
      .then((res) => {
        if (res.status === 200) {
          // console.log(res);
          setIsLoading(false);
          setUpdateResult(res);
        }
      })
      .catch((err) => {
        // console.log(err);
        setIsLoading(false);
      });
  };

  // SHOWING BULK UPDATE PROCESS
  const showBulkUpdate = () => {
    if (!validEntities.includes(model)) {
      setIsProcessing(false);
      return (
        <ErrorMessageBox message="Something wrong while getting the data. Please go back and try to filter data again!" />
      );
    } else if (!isBulkUpdateAccessAllowed) {
      return (
        <ErrorMessageBox message="You don't have permission to access this page" />
      );
    } else {
      return getStepContent(activeStep);
    }
  };

  return (
    <Layout sessionData={undefined}>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="lg" className={classes.container}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper>
                <Steppers steps={steps} activeStep={activeStep} />
              </Paper>
              <Box my={2}>
                <Formik<FormValues>
                  enableReinitialize
                  validateOnChange
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ handleSubmit }: FormikProps<FormValues>) => (
                    <Paper>
                      <StyledForm onSubmit={handleSubmit}>
                        <Box className={classes.root}>{showBulkUpdate()}</Box>

                        <ActionButtons
                          selectedItems={selectedItems}
                          isProcessing={isProcessing}
                          isLoading={isLoading}
                          activeStep={activeStep}
                          updateResult={updateResult}
                          updateFields={updateFields}
                          handleBack={handleBack}
                          handleNext={handleNext}
                          setShowConfirmation={setShowConfirmation}
                        />

                        <ConfirmLeaveDialog
                          open={showConfirmation}
                          onConfirm={handleConfirmLeaving}
                          onCancel={handleCancelLeaving}
                        />
                      </StyledForm>
                    </Paper>
                  )}
                </Formik>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </main>
    </Layout>
  );
}
